import type {PaymentCard} from '@yandex-delivery/api/model/payment'
import {Button, Image, Lead, Loader, Radio, Slot, Text, Trail, Typography} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {isEmpty} from '@yandex-delivery/utils'
import type {ReactElement} from 'react'
import {useCallback} from 'react'

import {useConfigContext} from '../../../../contexts/ConfigContext'
import {EventGoal, useFormUserEventHandler} from '../../../../lib/metrika'
import {getIconSrc} from '../PaymentCard/utils'
import * as classes from './PaymentCardButton.classes'

export type PaymentCardButtonProps = {
  errorText?: string
  isPending: boolean
  onPress?: VoidFunction
  paymentCard?: PaymentCard
}

export function PaymentCardButton(props: PaymentCardButtonProps): ReactElement {
  const {errorText, isPending, onPress, paymentCard} = props
  const i18n = useI18n()
  const config = useConfigContext()
  const unauthorized = isEmpty(config.launch?.passport_account)

  const handleReachGoal = useFormUserEventHandler()

  const handlePress = useCallback(() => {
    handleReachGoal(EventGoal.SelectPaymentCard)
    onPress?.()
  }, [handleReachGoal, onPress])

  return (
    <Slot size="l">
      <Lead
        // @ts-expect-error 😔 кривая типизация GWC, пропс наследуется вот Box
        justifyContent="initial"
        size="m"
      >
        {isPending ? (
          <Loader />
        ) : (
          <Image
            alt={paymentCard?.system}
            height={24}
            loading="lazy"
            src={getIconSrc(paymentCard?.system)}
            width={36}
          />
        )}
      </Lead>
      {!isPending && (
        <>
          <Text>
            <Typography group="tight" thickness="regular" variant="body2">
              {paymentCard?.number ? toCardView(paymentCard.number) : i18n('bdui.payment-method.card', 'Карта')}
            </Typography>
            {errorText && (
              <Typography color="error" data-error="true" group="tight" thickness="regular" variant="caption1">
                {errorText}
              </Typography>
            )}
          </Text>
          <Trail className={classes.trail}>
            {unauthorized ? (
              <Button
                className={classes.link}
                Component="a"
                href={config.getPassportUrl('/auth')}
                onPress={() => {
                  handleReachGoal(EventGoal.PaymentSignInClick)
                }}
                shape="circle"
                size="xs"
                variant="action"
              >
                {i18n('bdui.payment-method.login', 'Войти в Яндекс ID')}
              </Button>
            ) : (
              <Button
                onPress={handlePress}
                shape="circle"
                size="xs"
                variant={paymentCard?.number ? 'outline' : 'action'}
              >
                {paymentCard?.number
                  ? i18n('bdui.payment-method.change', 'Изменить')
                  : i18n('bdui.payment-method.select', 'Выбрать')}
              </Button>
            )}
          </Trail>
          {paymentCard?.number && (
            <Trail ml="s">
              <Radio checked />
            </Trail>
          )}
        </>
      )}
    </Slot>
  )
}

function toCardView(number?: string): string | undefined {
  /** распиливаем номер карты с маской на 4 блока по 4 цифры/звездочки */
  return number?.match(/.{1,4}/g)?.join(' ')
}
